import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { CheckCircle, Info, Loader2, Mic, MicOff, Send } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { autoFilingOutcome, describeOutcome, draftAfterSave } from '@cerebro-diem/core';
import type { CaptureSource } from '@cerebro-diem/core';
import { addPendingCapture, sendPendingCapture, useCaptureQueue } from '../services/pendingCaptures';
import { getSession } from '../services/supabase';
import { EdgeFunctionError, invokeEdgeFunction } from '../services/edgeFunctions';
import { errorMessage, isRecord } from '../services/errors';
import { useProfile } from '../services/profile';
import { queryKeys, refreshFilingViews } from '../services/queries';
import { useVoiceRecorder } from '../services/voiceRecorder';
import type { CaptureRecording } from '../services/captureStorage';
import { useAuthStore } from '../stores/authStore';
import { useUnsavedChanges } from './useUnsavedChanges';
import RecordingRecovery from './RecordingRecovery';

interface Notice {
  tone: 'success' | 'info';
  message: string;
}

const NOTICE_MS = 5000;

/** What the classify-capture response says happened. Nothing is called filed unless the response says it was. */
function autoFilingNotice(result: unknown): Notice {
  const outcome = autoFilingOutcome(isRecord(result) ? result.classification : null);
  if (outcome?.status === 'auto_filed') {
    return { tone: 'success', message: `Saved and filed as ${describeOutcome(outcome)}.` };
  }
  if (outcome?.status === 'needs_review') {
    return { tone: 'info', message: `Saved. AI suggests ${describeOutcome(outcome)}; confirm it in the Inbox.` };
  }
  return { tone: 'info', message: 'Saved. Not filed yet, so file it from the Inbox.' };
}

export default function CaptureInput() {
  const [text, setText] = useState('');
  const queue = useCaptureQueue();
  const userId = useAuthStore(state => state.user?.id);
  // Read when a recording is saved: what is in the box goes first in the same capture.
  const textRef = useRef(text);
  textRef.current = text;
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const isSavingRef = useRef(false);
  const latestCaptureIdRef = useRef<string | null>(null);
  const noticeTimerRef = useRef<number | undefined>(undefined);
  const queryClient = useQueryClient();

  const showNotice = (next: Notice | null) => {
    window.clearTimeout(noticeTimerRef.current);
    setNotice(next);
    if (next) noticeTimerRef.current = window.setTimeout(() => setNotice(null), NOTICE_MS);
  };

  useEffect(() => () => window.clearTimeout(noticeTimerRef.current), []);

  const autoFile = async (captureId: string) => {
    let next: Notice;
    try {
      next = autoFilingNotice(await invokeEdgeFunction('classify-capture', { capture_id: captureId }));
    } catch (err) {
      if (err instanceof EdgeFunctionError && err.status === 409) {
        // Filed by hand while auto-filing ran: nothing went wrong.
        void refreshFilingViews(queryClient);
        return;
      }
      next = {
        tone: 'info',
        message: `Saved, but not filed: ${errorMessage(err)}. File it from the Inbox.`,
      };
    }
    void refreshFilingViews(queryClient);
    // A newer capture's status replaces this one; do not overwrite it with an older result.
    if (latestCaptureIdRef.current === captureId) showNotice(next);
  };

  /** Browser acknowledgment precedes clearing input or any remote transcription/insertion. */
  const saveCapture = async (rawText: string, source: CaptureSource, recording?: CaptureRecording, owner?: string, recordingId?: string): Promise<boolean> => {
    if ((!rawText.trim() && !recording) || isSavingRef.current) return false;
    isSavingRef.current = true;
    setIsSaving(true);
    setError(null);
    showNotice(null);
    let kept = false;
    try {
      const { data: { session } } = await getSession();
      if (!session || (recording && session.user.id !== owner)) {
        throw new Error('Sign in as the account that made this capture before saving it.');
      }
      const pending = await addPendingCapture(session.user.id, rawText.trim(), source, recording, undefined, recordingId);
      kept = true;
      setText(prev => draftAfterSave(prev, rawText));
      latestCaptureIdRef.current = pending.id;
      const outcome = await sendPendingCapture(pending.id);
      if (outcome !== 'saved') {
        if (outcome !== 'busy' && outcome.serverSaved) {
          showNotice({ tone: 'info', message: `Saved to Cerebro; local cleanup is waiting: ${outcome.error}` });
        } else {
          showNotice({ tone: 'info', message: `Saved on this device, waiting to send${outcome === 'busy' ? '' : `: ${outcome.error}`}` });
        }
        return true;
      }
      showNotice({ tone: 'success', message: 'Saved' });
      void queryClient.invalidateQueries({ queryKey: queryKeys.captures });
      void autoFile(pending.id);
      return true;
    } catch (err) {
      setError(`${kept ? 'Saved on this device; sending could not finish' : 'Not saved on this device'}: ${errorMessage(err)}${kept ? '' : ' Keep this page open, or recover your input before closing it.'}`);
      return kept;
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  // Said beside the saved notice when a recording was stopped at its limit (CER-83).
  const [limitNote, setLimitNote] = useState<string | null>(null);
  // The person's tap-or-hold setting; holding until it is known (CER-175).
  const voiceMode = useProfile().data?.voice_mode ?? 'push_to_talk';
  const recorder = useVoiceRecorder({
    onStart: () => {
      setError(null);
      setLimitNote(null);
      showNotice(null);
    },
    onError: (reason) => setError(`Nothing was saved. ${reason}`),
    onLimit: setLimitNote,
    onRecording: async (recording, owner, id) => {
      const typed = textRef.current;
      return saveCapture(typed, 'voice', recording, owner, id);
    },
  }, voiceMode);
  const { isRecording, isTranscribing } = recorder;
  const recoveryAudio = recorder.recoveryRecording;

  const handleKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void saveCapture(text, 'text');
    }
  };

  const busy = isSaving || isTranscribing;
  useUnsavedChanges(!!text.trim() || !!recoveryAudio || isRecording || isSaving, { navigation: false });

  return (
    <div className="max-w-3xl mx-auto w-full">
      <div className="relative flex items-center gap-2">
        <div className="flex-1 min-w-0 relative">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="What's on your mind?"
            aria-label="Capture a thought"
            className="w-full px-4 py-3 pr-12 rounded-2xl border border-outline/30 bg-surface-variant/30
                       focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary
                       resize-none text-on-surface placeholder:text-on-surface-variant"
            rows={1}
            disabled={busy}
          />
          {/* 44px, inside the textarea's right padding (pr-12). */}
          <button
            type="button"
            onClick={() => void saveCapture(text, 'text')}
            disabled={!text.trim() || busy || !!recoveryAudio}
            aria-label="Save capture"
            className="absolute right-1 top-1/2 -translate-y-1/2 p-3 rounded-full
                       text-primary hover:bg-primary-container disabled:opacity-50 disabled:cursor-not-allowed
                       transition-colors"
          >
            {isSaving ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
          </button>
        </div>

        <button
          type="button"
          {...recorder.pressHandlers}
          disabled={busy || (!!recoveryAudio && !isRecording)}
          aria-label={isRecording ? `${recorder.words.stop} recording` : `${recorder.words.verb} to record`}
          className={`shrink-0 p-4 rounded-full transition-all touch-none select-none ${
            isRecording
              ? 'bg-error text-on-error scale-110 animate-pulse'
              : busy
                ? 'bg-primary/50 text-on-primary'
                : 'bg-primary text-on-primary hover:bg-primary/90 active:scale-95'
          } disabled:cursor-not-allowed`}
        >
          {isTranscribing ? (
            <Loader2 size={24} className="animate-spin" />
          ) : isRecording ? (
            <MicOff size={24} />
          ) : (
            <Mic size={24} />
          )}
        </button>
      </div>

      {!isRecording && <Link to="/import" className="inline-flex min-h-11 items-center text-primary underline">Import meeting</Link>}

      {isRecording && (
        <p className="text-center text-sm text-on-surface-variant mt-2 animate-pulse">{recorder.words.recording}</p>
      )}

      {isTranscribing && <p className="text-center text-sm text-on-surface-variant mt-2">Transcribing...</p>}

      {limitNote && <p role="status" className="text-center text-sm text-on-surface-variant mt-2">{limitNote}</p>}

      {isSaving && <p className="text-center text-sm text-on-surface-variant mt-2">Saving...</p>}

      {queue.error && <p role="alert" className="text-sm text-error mt-2">{queue.error}</p>}
      {recoveryAudio && <div className="mt-3 space-y-2">
        <p role="alert">Retry saving or download the original audio. If storage failed, keep this page open until recovery succeeds.</p>
        <RecordingRecovery ownerId={userId} blob={recoveryAudio.blob} />
        <button type="button" disabled={busy} onClick={() => void recorder.retry()} className="text-primary underline">Retry saving recording</button>
      </div>}
      {error && (
        <p role="alert" className="text-center text-sm text-error mt-2">
          {error}
        </p>
      )}

      {notice && (
        // A paragraph, not a flex row, so a long notice wraps under its icon.
        <p role="status" className={`text-center text-sm mt-2 ${notice.tone === 'success' ? 'text-success' : 'text-on-surface-variant'}`}>
          {notice.tone === 'success' ? (
            <CheckCircle size={16} className="inline mr-1 align-text-bottom" aria-hidden="true" />
          ) : (
            <Info size={16} className="inline mr-1 align-text-bottom" aria-hidden="true" />
          )}
          {notice.message}
        </p>
      )}
    </div>
  );
}
